import type { AppExportConnector } from '@appStore/reducers/app'
import { exportContactsModule } from '../../import-runner/exportContactsModule.js'
import { csvContactsParserFactory } from '../../import-runner/common/csvContactsParserFactory.js'
import { contactsExportType } from '../../import-runner/const/contactsExportType.js'
import {
	isValidLinkedInEventAttendeesUrl,
	isValidLinkedInGroupUrl,
	isValidLinkedInSearchUrl,
	isValidSalesNavigatorLeadsListUrl,
	isValidSalesNavigatorSearchUrl
} from '../../import-runner/const/exportUrlPatterns.js'
import { InvalidOperationError } from '../../import-runner/common/errors/InvalidOperationError.js'
import { eventNames as importRunnerEventNames } from '../../import-runner/const/eventNames.js'
import { appConnector } from './singleton'

const eventNames = {
	...importRunnerEventNames,
	exportStopRequested: 'exportStopRequested'
} as const

export function createAppExportConnector(): AppExportConnector {
	const _eventHandlers: Record<
		string,
		((...args: unknown[]) => void | Promise<unknown>)[]
	> = {}

	function addEventHandler(
		eventName: string,
		handler: (...args: unknown[]) => void | Promise<unknown>
	): void {
		if (!_eventHandlers[eventName]) {
			_eventHandlers[eventName] = []
		}
		_eventHandlers[eventName].push(handler)
	}

	function removeEventHandler(eventName: string): void {
		_eventHandlers[eventName] = []
	}

	async function broadcastSnailyEvent(snailyEvent: {
		event: string
		args?: unknown
	}): Promise<void> {
		const handlers = _eventHandlers[snailyEvent.event]
		if (!handlers) {
			return
		}
		const args =
			snailyEvent.args ??
			(snailyEvent as { event?: { args?: unknown } }).event?.args
		for (const handler of handlers) {
			if (await handler(args)) {
				return
			}
		}
	}

	if (typeof document !== 'undefined') {
		document.addEventListener('SnailyEvent', async (e: Event) => {
			await broadcastSnailyEvent((e as CustomEvent).detail)
		})
	}

	async function onExportStopRequestedHandler(args: {
		metadata?: object
	}): Promise<void> {
		const metadata = args?.metadata ?? appConnector.utils.createMetadata()
		await exportContactsModule.stop({ metadata })
	}

	addEventHandler(eventNames.exportStopRequested, (...args: unknown[]) =>
		onExportStopRequestedHandler((args[0] ?? {}) as { metadata?: object })
	)

	async function startFromUrl(
		type: string,
		url: string,
		tag: object,
		metadata?: object
	): Promise<unknown> {
		if (!type) {
			throw new InvalidOperationError('type is not defined', metadata)
		}
		if (!url) {
			throw new InvalidOperationError('url is not defined', metadata)
		}
		const meta = metadata ?? appConnector.utils.createMetadata()
		const result = exportContactsModule.start({
			parameter: { type, details: { url, tag: tag || {} } },
			metadata: meta
		})
		return result
	}

	const profilesExport: AppExportConnector['profilesExport'] = {
		extractLinkedInContacts(
			source: unknown,
			metadata?: object
		): Promise<unknown> {
			const result = Promise.resolve(
				csvContactsParserFactory().parseLinkedInContacts(source, metadata)
			)
			return result
		},
		extractFullLinkedInContacts(
			source: unknown,
			metadata?: object
		): Promise<unknown> {
			const result = Promise.resolve(
				csvContactsParserFactory().parseFullLinkedInContacts(source, metadata)
			)
			return result
		},
		async startCSV(source: unknown, metadata?: object): Promise<unknown> {
			if (!source) {
				throw new InvalidOperationError('contacts are not set', metadata)
			}
			const meta = metadata ?? appConnector.utils.createMetadata()
			const result = exportContactsModule.start({
				parameter: {
					type: contactsExportType.csvContactsList,
					details: { contacts: source }
				},
				metadata: meta
			})
			return result
		},
		linkedInSearch: {
			isValid(url: string) {
				const result = isValidLinkedInSearchUrl(url)
				return result
			},
			async count(url: string, metadata?: object): Promise<unknown> {
				if (!isValidLinkedInSearchUrl(url)) {
					throw new InvalidOperationError(
						'Invalid url, call isValid first',
						metadata
					)
				}
				const result = exportContactsModule.countProfilesInLinkedInSearch({
					parameter: { url },
					metadata: metadata ?? appConnector.utils.createMetadata()
				})
				return result
			},
			async start(url: string, metadata?: object): Promise<void> {
				if (!isValidLinkedInSearchUrl(url)) {
					throw new InvalidOperationError(
						'Invalid url, call isValid first',
						metadata
					)
				}
				await startFromUrl(
					contactsExportType.linkedInContactsSearchPage,
					url,
					{},
					metadata
				)
			}
		},
		linkedInGroup: {
			isValid(url: string) {
				const result = isValidLinkedInGroupUrl(url)
				return result
			},
			async count(url: string, metadata?: object): Promise<unknown> {
				if (!isValidLinkedInGroupUrl(url)) {
					throw new InvalidOperationError(
						'Invalid url, call isValid first',
						metadata
					)
				}
				const result = exportContactsModule.countProfilesInLinkedInGroup({
					parameter: { url },
					metadata: metadata ?? appConnector.utils.createMetadata()
				})
				return result
			},
			async start(url: string, metadata?: object): Promise<void> {
				if (!isValidLinkedInGroupUrl(url)) {
					throw new InvalidOperationError(
						'Invalid url, call isValid first',
						metadata
					)
				}
				await startFromUrl(
					contactsExportType.linkedInGroupContactsPage,
					url,
					{
						typeaheadQuery: ''
					},
					metadata
				)
			}
		},
		linkedInEvent: {
			isValid(url: string) {
				const result = isValidLinkedInEventAttendeesUrl(url)
				return result
			},
			async count(url: string, metadata?: object): Promise<unknown> {
				if (!isValidLinkedInEventAttendeesUrl(url)) {
					throw new InvalidOperationError(
						'Invalid url, call isValid first',
						metadata
					)
				}
				const result = exportContactsModule.countProfilesInLinkedInSearch({
					parameter: { url },
					metadata: metadata ?? appConnector.utils.createMetadata()
				})
				return result
			},
			async start(url: string, metadata?: object): Promise<void> {
				if (!isValidLinkedInEventAttendeesUrl(url)) {
					throw new InvalidOperationError(
						'Invalid url, call isValid first',
						metadata
					)
				}
				await startFromUrl(
					contactsExportType.linkedInContactsSearchPage,
					url,
					{},
					metadata
				)
			}
		},
		salesNavigator: {
			isValid(url: string) {
				const result =
					isValidSalesNavigatorSearchUrl(url) ||
					isValidSalesNavigatorLeadsListUrl(url)
				return result
			},
			async count(url: string, metadata?: object): Promise<unknown> {
				const meta = metadata ?? appConnector.utils.createMetadata()
				if (isValidSalesNavigatorSearchUrl(url)) {
					const result = exportContactsModule.countProfilesInSalesNavigatorSearch(
						{
							parameter: { url },
							metadata: meta
						}
					)
					return result
				}
				if (isValidSalesNavigatorLeadsListUrl(url)) {
					const result =
						exportContactsModule.countProfilesInSalesNavigatorLeadsList({
							parameter: { url },
							metadata: meta
						})
					return result
				}
				throw new InvalidOperationError('Invalid url, call isValid first', meta)
			},
			async start(url: string, metadata?: object): Promise<void> {
				if (isValidSalesNavigatorSearchUrl(url)) {
					await startFromUrl(
						contactsExportType.salesNavigatorContactsSearchPage,
						url,
						{},
						metadata
					)
					return
				}
				if (isValidSalesNavigatorLeadsListUrl(url)) {
					await startFromUrl(
						contactsExportType.salesNavigatorLeadsListPage,
						url,
						{},
						metadata
					)
					return
				}
				throw new InvalidOperationError(
					'Invalid url, call isValid first',
					metadata ?? appConnector.utils.createMetadata()
				)
			}
		},
		linkedInMyConnections: {
			async count(_url: string, metadata?: object): Promise<unknown> {
				const result = exportContactsModule.countProfilesInLinkedInMyConnections({
					parameter: {},
					metadata: metadata ?? appConnector.utils.createMetadata()
				})
				return result
			},
			async start(_url: string, metadata?: object): Promise<void> {
				const meta = metadata ?? appConnector.utils.createMetadata()
				const result = exportContactsModule.start({
					parameter: {
						type: contactsExportType.linkedInMyConnections,
						details: {}
					},
					metadata: meta
				})
				return result
			}
		},
		async start(contacts: unknown, metadata?: object): Promise<unknown> {
			const result = profilesExport.startCSV(contacts, metadata)
			return result
		},
		async stop(metadata?: object): Promise<void> {
			await exportContactsModule.stop({
				metadata: metadata ?? appConnector.utils.createMetadata()
			})
		},
		async clear(metadata?: object): Promise<void> {
			await exportContactsModule.clear({
				metadata: metadata ?? appConnector.utils.createMetadata()
			})
		},
		getState(): unknown {
			const result = exportContactsModule.getState()
			return result
		},
		addExportStartingHandler(handler: () => void): void {
			addEventHandler(eventNames.exportStarting, handler)
		},
		addExportStartedHandler(handler: () => void): void {
			addEventHandler(eventNames.exportStarted, handler)
		},
		addExportChangedHandler(handler: (e?: unknown) => void): void {
			addEventHandler(eventNames.exportChanged, handler)
		},
		addExportFailedHandler(handler: () => void): void {
			addEventHandler(eventNames.exportFailed, handler)
		},
		addExportCompletedHandler(handler: () => void): void {
			addEventHandler(eventNames.exportCompleted, handler)
		},
		removedAllHandlers(): void {
			removeEventHandler(eventNames.exportStarting)
			removeEventHandler(eventNames.exportStarted)
			removeEventHandler(eventNames.exportCleared)
			removeEventHandler(eventNames.exportChanged)
			removeEventHandler(eventNames.exportFailed)
			removeEventHandler(eventNames.exportCompleting)
			removeEventHandler(eventNames.exportCompleted)
		}
	}

	return {
		profilesExport
	}
}
